import React from 'react';

/**
 * Logo Component — CETTO SYSTEM
 *
 * Versão baseada em texto HTML real (sem SVG-cramado), garantindo que:
 *  - Nunca corta (não há viewBox fixo).
 *  - Escala de forma proporcional via prop `size`.
 *  - O "C" é verde isolado, "ETTO" segue na cor do texto, "SYSTEM" abaixo em verde.
 *
 * Mudança jun/26:
 *  - Tipografia trocada de Inter → Syne (matching com toda a UI v2 do
 *    Dashboard, Register e Conversas — já carregada via Google Fonts
 *    no index.html).
 *  - Default size 140 → 115. Logo fica mais discreto, deixa espaço
 *    visual pro resto do shell respirar. Por ser proporcional, qualquer
 *    chamada que passa size= explícito continua funcionando igual.
 *  - Pesos ajustados pra harmonizar com Syne (800 no CETTO, 700 no
 *    SYSTEM) — Syne tem traço geométrico mais delicado que Inter, pesos
 *    900/500 antigos ficavam contrastantes demais.
 *  - Letter-spacing do CETTO um tiquinho menor (Syne não precisa de
 *    spacing aberto como Inter).
 *
 * Props (mesma API):
 *  - size:         Largura total alvo em px (default: 115).
 *  - light:        true → texto branco (fundo escuro).
 *                  false → texto azul-escuro (fundo claro).
 *  - primaryColor: Cor do "C" e do "SYSTEM" (default: #25d366).
 *  - className / style: pass-through opcionais.
 */
const Logo = ({
  size = 85,
  light = true,
  primaryColor = '#25d366',
  className = '',
  style = {}
}) => {
  const textColor = light ? '#FFFFFF' : '#0a0f1a';

  // Proporções relativas ao tamanho total (largura).
  // - cettoSize: mantido 0.32 do total.
  // - systemSize: bumpado de 0.24 → 0.30 do CETTO. Sem isso, com o
  //   default menor (85), o SYSTEM ficaria 6.5px e perderia legibilidade.
  // - gap: idem (escala junto com cettoSize).
  const cettoSize = size * 0.32;          // size=85 → CETTO 27.2px
  const systemSize = cettoSize * 0.30;    // CETTO 27.2 → SYSTEM 8.16px
  const gap = Math.max(2, cettoSize * 0.08);

  // Syne já está pré-carregado via Google Fonts no index.html (mesma
  // família usada em Dashboard, Register, Conversas, etc.). Fallback
  // genérico defensivo pra quando o webfont ainda não baixou.
  const fontFamily = "'Syne', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

  return (
    <div
      className={className}
      aria-label="Cetto System"
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        userSelect: 'none',
        lineHeight: 1,
        ...style
      }}
    >
      {/* CETTO — peso 800 + spacing mínimo (Syne é geométrico) */}
      <div
        style={{
          fontFamily,
          fontSize: `${cettoSize}px`,
          fontWeight: 800,
          letterSpacing: '0.01em',
          lineHeight: 1,
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ color: primaryColor }}>C</span>
        <span style={{ color: textColor }}>ETTO</span>
      </div>

      {/* SYSTEM — peso 700 (era 500), spacing aberto pra ficar legível
          mesmo em tamanho pequeno */}
      <div
        style={{
          fontFamily,
          fontSize: `${systemSize}px`,
          fontWeight: 700,
          letterSpacing: '0.45em',
          color: primaryColor,
          marginTop: `${gap}px`,
          paddingLeft: '2px',
          whiteSpace: 'nowrap',
          textTransform: 'uppercase'
        }}
      >
        System
      </div>
    </div>
  );
};

export default Logo;
